import { useState } from "react";

// Builds the full stored shape for one account from a (possibly partial) payload —
// shared by single-account creation and bulk CSV import so both paths stay in sync.
function buildAccount(account,id){
  const hasVolume=!!account.invoiceFormat?.hasVolume;
  const hasWeight=!!account.invoiceFormat?.hasWeight;
  return{
    id,
    name:account.name||"",
    haulerName:account.haulerName||"",
    stream:account.stream||"MSW",
    invoiceFormat:{
      hasVolume,
      volumeUnit:hasVolume?"cubic_yards":null,
      hasWeight,
      weightUnit:hasWeight?(account.invoiceFormat?.weightUnit||"tons"):null,
    },
    lineItemMappings:[],
    accountNumber:account.accountNumber||"",
    notes:account.notes||"",
    containerType:account.containerType||"",
    containerQuantity:account.containerQuantity??null,
    containerSize:account.containerSize??null,
    sizeUnit:account.sizeUnit||null,
    weeklyPickupFrequency:account.weeklyPickupFrequency??null,
  };
}

// Owns wasteAccounts: the hauler-account registry that connects invoice parsing to
// historical waste data. Each account remembers how its invoices are formatted and
// which line-item labels map to which stream, learned over time as invoices are processed.
export function useWasteAccounts(){
  const [wasteAccounts,setWasteAccounts]=useState([]);

  const newAccountId=(suffix="")=>"wacct_"+Date.now()+suffix;

  const addAccount=(account)=>{
    const id=newAccountId();
    setWasteAccounts(prev=>[...prev,buildAccount(account,id)]);
    return id;
  };

  // Appends many accounts in a single state update (bulk CSV setup) — each row gets a
  // guaranteed-unique id even though they're all created within the same tick.
  const addAccountsBulk=(accountList)=>{
    const withIds=accountList.map((account,i)=>buildAccount(account,newAccountId(`_${i}`)));
    setWasteAccounts(prev=>[...prev,...withIds]);
  };

  const updateAccount=(id,patch)=>{
    setWasteAccounts(prev=>prev.map(a=>{
      if(a.id!==id) return a;
      const next={...a,...patch};
      if(patch.invoiceFormat){
        const hasVolume=!!patch.invoiceFormat.hasVolume;
        const hasWeight=!!patch.invoiceFormat.hasWeight;
        next.invoiceFormat={
          hasVolume,
          volumeUnit:hasVolume?"cubic_yards":null,
          hasWeight,
          weightUnit:hasWeight?(patch.invoiceFormat.weightUnit||"tons"):null,
        };
      }
      return next;
    }));
  };

  const removeAccount=(id)=>{
    setWasteAccounts(prev=>prev.filter(a=>a.id!==id));
  };

  // Appends newly-learned rawLabel→type/stream mappings so future invoices from this
  // account recognize the same line items automatically.
  const addLineItemMappings=(accountId,mappings)=>{
    if(!mappings.length) return;
    setWasteAccounts(prev=>prev.map(a=>a.id===accountId?{...a,lineItemMappings:[...(a.lineItemMappings||[]),...mappings]}:a));
  };

  return {
    wasteAccounts,setWasteAccounts,
    addAccount,addAccountsBulk,updateAccount,removeAccount,
    addLineItemMappings,
  };
}
